// The frame check gates the latch, so a top-level tab can't fake embedded mode.
const EMBED_FLAG_KEY = 'hc-extract-embedded';

const isFramed = (): boolean => typeof window !== 'undefined' && window.parent !== window;

export const isEmbedded = (): boolean => {
  if (!isFramed()) return false;

  try {
    if (sessionStorage.getItem(EMBED_FLAG_KEY) === 'true') return true;
  } catch {
    /* empty */
  }

  const flagged = new URLSearchParams(window.location.search).get('embed') === 'true';
  if (flagged) {
    try {
      sessionStorage.setItem(EMBED_FLAG_KEY, 'true');
    } catch {
      /* empty */
    }
  }
  return flagged;
};

// Unlatched, unlike embedded mode: the intent has to expire with the entry URL.
export const isReopenIntent = (): boolean =>
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('reopen') === '1';

// Embedded, the host's chrome already draws the border, radius and shadow around the frame, so a
// surface that fills it must not draw a second one inside.
export const cardSurfaceClass = (isEmbedded: boolean): string =>
  isEmbedded ? 'bg-card overflow-hidden' : 'bg-card rounded-xl shadow-lg overflow-hidden';

// `curation` is the DG step on its own, for a Validate panel that only curates.
export type EmbedSurface = 'full' | 'curation';

const EMBED_SURFACE_KEY = 'hc-extract-embed-surface';

// Latched, because the host builds the entry src and in-app navigation drops its query string.
// Cleared by an entry that omits it: two Extract frames share sessionStorage.
const resolveSurface = (): EmbedSurface => {
  if (typeof window === 'undefined') return 'full';

  const params = new URLSearchParams(window.location.search);
  const fromHost = params.has('embed');
  const requested: EmbedSurface = params.get('surface') === 'curation' ? 'curation' : 'full';

  try {
    if (!fromHost)
      return sessionStorage.getItem(EMBED_SURFACE_KEY) === 'curation' ? 'curation' : 'full';
    if (requested === 'curation') sessionStorage.setItem(EMBED_SURFACE_KEY, 'curation');
    else sessionStorage.removeItem(EMBED_SURFACE_KEY);
  } catch {
    /* empty */
  }

  return requested;
};

// Resolved at load: the host's query string is only on the URL until the first navigation.
const surface = resolveSurface();

export const embedSurface = (): EmbedSurface => surface;
